import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Search,
  BookOpen,
  HelpCircle,
  LayoutDashboard,
  Package,
  Receipt,
  ClipboardCheck,
  Settings,
  BookText,
  Calculator,
  ChevronRight,
  Image,
  ArrowRight,
  TrendingDown,
  Plus,
  RefreshCw,
  Lightbulb,
  Info,
  FileText,
  Mail,
  MessageCircle,
  ExternalLink,
  CheckCircle2,
  Circle,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { EvoFeatureFeedbackCollector } from '@/components/FeedbackCollector';

interface DocSection {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  color: string;
}

interface StepGuide {
  step: number;
  title: string;
  description: string;
  hasScreenshot?: boolean;
}

interface GlossaryTerm {
  term: string;
  definition: string;
  category: string;
}

interface CalculationMethod {
  id: string;
  name: string;
  formula: string;
  description: string;
  example: {
    assetCost: number;
    salvageValue: number;
    usefulLife: number;
    calculations: string[];
  };
}

const docSections: DocSection[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: BookOpen,
    description: 'Learn the basics of Access Assets and get up and running quickly',
    color: 'blue',
  },
  {
    id: 'dashboard',
    title: 'Dashboard',
    icon: LayoutDashboard,
    description: 'Understanding your dashboard widgets and KPIs',
    color: 'purple',
  },
  {
    id: 'assets',
    title: 'Assets',
    icon: Package,
    description: 'Managing your asset register, creating and editing assets',
    color: 'teal',
  },
  {
    id: 'transactions',
    title: 'Transactions',
    icon: Receipt,
    description: 'Depreciation, additions, revaluations, and disposals',
    color: 'orange',
  },
  {
    id: 'audits',
    title: 'Audits',
    icon: ClipboardCheck,
    description: 'Physical asset verification and audit management',
    color: 'green',
  },
  {
    id: 'settings',
    title: 'Settings & Administration',
    icon: Settings,
    description: 'Configure categories, locations, and system options',
    color: 'indigo',
  },
  {
    id: 'glossary',
    title: 'Glossary',
    icon: BookText,
    description: 'Key terms and definitions used throughout the application',
    color: 'pink',
  },
  {
    id: 'calculations',
    title: 'Calculations',
    icon: Calculator,
    description: 'Depreciation methods and calculation formulas explained',
    color: 'red',
  },
];

const glossaryTerms: GlossaryTerm[] = [
  { term: 'Acquisition Cost', definition: 'The original purchase price of an asset, including all costs necessary to bring the asset to its intended use (e.g., delivery, installation, taxes).', category: 'Financial' },
  { term: 'Accumulated Depreciation', definition: 'The total amount of depreciation expense that has been recorded against an asset since it was acquired.', category: 'Financial' },
  { term: 'Asset Code', definition: 'A unique identifier assigned to each asset for tracking and reference purposes.', category: 'General' },
  { term: 'Asset Register', definition: 'A comprehensive list of all fixed assets owned by an organisation, including details such as cost, location, and depreciation.', category: 'General' },
  { term: 'Barcode', definition: 'A machine-readable code assigned to an asset for quick identification during audits and tracking.', category: 'Tracking' },
  { term: 'Category', definition: 'A classification grouping for assets based on their type or nature (e.g., Computer Equipment, Furniture, Vehicles).', category: 'General' },
  { term: 'Depreciation', definition: "The systematic allocation of an asset's cost over its useful life to reflect its declining value.", category: 'Financial' },
  { term: 'Depreciation Method', definition: 'The approach used to calculate depreciation (e.g., Straight-Line, Declining Balance, Sum of Years Digits).', category: 'Financial' },
  { term: 'Disposal', definition: 'The process of removing an asset from the register when it is sold, scrapped, or otherwise retired from use.', category: 'Transactions' },
  { term: 'Net Book Value (NBV)', definition: 'The current value of an asset calculated as Acquisition Cost minus Accumulated Depreciation.', category: 'Financial' },
  { term: 'Nominal Account', definition: 'A general ledger account used to record financial transactions related to assets.', category: 'Financial' },
  { term: 'Physical Audit', definition: 'A verification process where assets are physically inspected to confirm their existence, location, and condition.', category: 'Audits' },
  { term: 'QR Code', definition: 'A two-dimensional barcode that can store more information than traditional barcodes, used for asset identification.', category: 'Tracking' },
  { term: 'Residual Value', definition: 'The estimated value of an asset at the end of its useful life, also known as salvage value.', category: 'Financial' },
  { term: 'Revaluation', definition: "The process of adjusting an asset's book value to reflect its current fair market value.", category: 'Transactions' },
  { term: 'Useful Life', definition: 'The estimated period over which an asset is expected to be usable by the organisation.', category: 'Financial' },
];

const calculationMethods: CalculationMethod[] = [
  {
    id: 'straight-line',
    name: 'Straight-Line Depreciation',
    formula: '(Cost - Residual Value) / Useful Life',
    description: "The most common method that spreads the cost evenly over the asset's useful life. Each period receives the same depreciation expense.",
    example: {
      assetCost: 10000,
      salvageValue: 1000,
      usefulLife: 5,
      calculations: [
        'Annual Depreciation = (£10,000 - £1,000) / 5 years',
        'Annual Depreciation = £9,000 / 5',
        'Annual Depreciation = £1,800 per year',
        '',
        'Year 1: NBV = £10,000 - £1,800 = £8,200',
        'Year 2: NBV = £8,200 - £1,800 = £6,400',
        'Year 3: NBV = £6,400 - £1,800 = £4,600',
        'Year 4: NBV = £4,600 - £1,800 = £2,800',
        'Year 5: NBV = £2,800 - £1,800 = £1,000 (Residual Value)',
      ],
    },
  },
  {
    id: 'declining-balance',
    name: 'Declining Balance Depreciation',
    formula: 'Book Value x Depreciation Rate',
    description: 'An accelerated method that applies a fixed percentage to the declining book value each period. Results in higher depreciation in early years.',
    example: {
      assetCost: 10000,
      salvageValue: 1000,
      usefulLife: 5,
      calculations: [
        'Using 40% declining balance rate (double the straight-line rate of 20%)',
        '',
        'Year 1: £10,000 x 40% = £4,000 depreciation, NBV = £6,000',
        'Year 2: £6,000 x 40% = £2,400 depreciation, NBV = £3,600',
        'Year 3: £3,600 x 40% = £1,440 depreciation, NBV = £2,160',
        'Year 4: £2,160 x 40% = £864 depreciation, NBV = £1,296',
        'Year 5: £1,296 - £1,000 = £296 depreciation, NBV = £1,000 (Residual Value)',
        '',
        'Note: In the final year, depreciation is limited to bring NBV to residual value.',
      ],
    },
  },
  {
    id: 'sum-of-years',
    name: 'Sum of Years Digits',
    formula: '(Remaining Life / Sum of Years) x (Cost - Residual Value)',
    description: 'An accelerated method that uses a fraction based on the remaining useful life. Provides higher depreciation in early years.',
    example: {
      assetCost: 10000,
      salvageValue: 1000,
      usefulLife: 5,
      calculations: [
        'Sum of Years = 5 + 4 + 3 + 2 + 1 = 15',
        'Depreciable Amount = £10,000 - £1,000 = £9,000',
        '',
        'Year 1: (5/15) x £9,000 = £3,000 depreciation, NBV = £7,000',
        'Year 2: (4/15) x £9,000 = £2,400 depreciation, NBV = £4,600',
        'Year 3: (3/15) x £9,000 = £1,800 depreciation, NBV = £2,800',
        'Year 4: (2/15) x £9,000 = £1,200 depreciation, NBV = £1,600',
        'Year 5: (1/15) x £9,000 = £600 depreciation, NBV = £1,000 (Residual Value)',
      ],
    },
  },
  {
    id: 'units-of-production',
    name: 'Units of Production',
    formula: '(Cost - Residual Value) / Total Units x Units Produced',
    description: 'Depreciation based on actual usage or output. Ideal for assets where wear is directly related to production volume.',
    example: {
      assetCost: 10000,
      salvageValue: 1000,
      usefulLife: 5,
      calculations: [
        'Assume total estimated production: 100,000 units',
        'Depreciation per unit = (£10,000 - £1,000) / 100,000 = £0.09 per unit',
        '',
        'Year 1: 25,000 units x £0.09 = £2,250 depreciation',
        'Year 2: 22,000 units x £0.09 = £1,980 depreciation',
        'Year 3: 20,000 units x £0.09 = £1,800 depreciation',
        'Year 4: 18,000 units x £0.09 = £1,620 depreciation',
        'Year 5: 15,000 units x £0.09 = £1,350 depreciation',
        '',
        'Total: 100,000 units = £9,000 total depreciation',
      ],
    },
  },
];

function ScreenshotPlaceholder({ title, aspectRatio = '16/9' }: { title: string; aspectRatio?: string }) {
  return (
    <div
      className={cn(
        'relative rounded-lg border-2 border-dashed border-muted-foreground/25 bg-muted/30',
        'flex flex-col items-center justify-center gap-2 p-8',
        'hover:border-muted-foreground/40 hover:bg-muted/40 transition-colors'
      )}
      style={{ aspectRatio }}
    >
      <Image className="h-12 w-12 text-muted-foreground/50" />
      <span className="text-sm text-muted-foreground/70 text-center">{title}</span>
      <Badge variant="outline" className="text-xs">Screenshot Placeholder</Badge>
    </div>
  );
}

function StepByStepGuide({ steps, title }: { steps: StepGuide[]; title: string }) {
  return (
    <div className="space-y-4">
      <h4 className="font-semibold text-lg flex items-center gap-2">
        <FileText className="h-5 w-5 text-primary" />
        {title}
      </h4>
      <div className="space-y-6">
        {steps.map((step, index) => (
          <div key={step.step} className="relative pl-8">
            <div className="absolute left-0 top-0 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-semibold">
              {step.step}
            </div>
            {index < steps.length - 1 && (
              <div className="absolute left-[11px] top-6 h-full w-0.5 bg-border" />
            )}
            <div className="space-y-3 pb-4">
              <h5 className="font-medium">{step.title}</h5>
              <p className="text-sm text-muted-foreground">{step.description}</p>
              {step.hasScreenshot && (
                <ScreenshotPlaceholder title={`Step ${step.step}: ${step.title}`} />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, description, color }: { icon: React.ComponentType<{ className?: string }>; title: string; description: string; color: string }) {
  const colorClasses: Record<string, string> = {
    blue: 'from-blue-500/10 to-blue-500/5 border-blue-200 dark:border-blue-800',
    purple: 'from-purple-500/10 to-purple-500/5 border-purple-200 dark:border-purple-800',
    teal: 'from-teal-500/10 to-teal-500/5 border-teal-200 dark:border-teal-800',
    orange: 'from-orange-500/10 to-orange-500/5 border-orange-200 dark:border-orange-800',
    green: 'from-green-500/10 to-green-500/5 border-green-200 dark:border-green-800',
    indigo: 'from-indigo-500/10 to-indigo-500/5 border-indigo-200 dark:border-indigo-800',
    pink: 'from-pink-500/10 to-pink-500/5 border-pink-200 dark:border-pink-800',
    red: 'from-red-500/10 to-red-500/5 border-red-200 dark:border-red-800',
  };

  const iconColorClasses: Record<string, string> = {
    blue: 'text-blue-600 dark:text-blue-400',
    purple: 'text-purple-600 dark:text-purple-400',
    teal: 'text-teal-600 dark:text-teal-400',
    orange: 'text-orange-600 dark:text-orange-400',
    green: 'text-green-600 dark:text-green-400',
    indigo: 'text-indigo-600 dark:text-indigo-400',
    pink: 'text-pink-600 dark:text-pink-400',
    red: 'text-red-600 dark:text-red-400',
  };

  return (
    <Card className={cn('relative overflow-hidden border-2 transition-all hover:shadow-md', colorClasses[color])}>
      <div className={cn('absolute inset-0 bg-gradient-to-br opacity-40', colorClasses[color])} />
      <CardHeader className="relative pb-2">
        <Icon className={cn('h-8 w-8 mb-2', iconColorClasses[color])} />
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="relative">
        <p className="text-sm text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
}

function SectionNavItem({ section, isActive, onClick }: { section: DocSection; isActive: boolean; onClick: () => void }) {
  const Icon = section.icon;
  const colorClasses: Record<string, string> = {
    blue: 'text-blue-600 dark:text-blue-400',
    purple: 'text-purple-600 dark:text-purple-400',
    teal: 'text-teal-600 dark:text-teal-400',
    orange: 'text-orange-600 dark:text-orange-400',
    green: 'text-green-600 dark:text-green-400',
    indigo: 'text-indigo-600 dark:text-indigo-400',
    pink: 'text-pink-600 dark:text-pink-400',
    red: 'text-red-600 dark:text-red-400',
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all',
        'hover:bg-muted/50',
        isActive && 'bg-primary/10 border border-primary/20'
      )}
    >
      <Icon className={cn('h-5 w-5 shrink-0', isActive ? 'text-primary' : colorClasses[section.color])} />
      <div className="flex-1 min-w-0">
        <div className={cn('font-medium text-sm truncate', isActive && 'text-primary')}>{section.title}</div>
      </div>
      {isActive && <ChevronRight className="h-4 w-4 text-primary shrink-0" />}
    </button>
  );
}

function GettingStartedContent() {
  const quickStartSteps: StepGuide[] = [
    { step: 1, title: 'Navigate to Assets', description: "Click on 'Assets' in the sidebar to view your asset register. This is where you can see all your organisation's fixed assets.", hasScreenshot: true },
    { step: 2, title: 'Create Your First Asset', description: "Click the 'Add Asset' button to create a new asset. Fill in the required fields including Asset Code, Name, Category, and Acquisition Cost.", hasScreenshot: true },
    { step: 3, title: 'Configure Categories', description: 'Go to Administration > Categories to set up asset categories with default depreciation settings.', hasScreenshot: true },
    { step: 4, title: 'Set Up Locations', description: 'Navigate to Administration > Locations to define where your assets are located.', hasScreenshot: true },
    { step: 5, title: 'Run Your First Depreciation', description: 'Go to Transactions > Depreciation to calculate and post depreciation for your assets.', hasScreenshot: true },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-2">Getting Started with Access Assets</h2>
        <p className="text-muted-foreground">
          Welcome to Access Assets, your comprehensive solution for fixed asset management. This guide will help you get up and running quickly.
        </p>
      </div>

      <Card className="relative overflow-hidden border-2 border-blue-200 dark:border-blue-800">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-500/5 opacity-40" />
        <CardHeader className="relative">
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-blue-600" />
            What is Access Assets?
          </CardTitle>
        </CardHeader>
        <CardContent className="relative space-y-4">
          <p className="text-sm text-muted-foreground">
            Access Assets is an enterprise asset management system designed to help organisations track, manage, and report on their fixed assets throughout their entire lifecycle. From acquisition to disposal, Access Assets provides the tools you need to maintain accurate records and ensure compliance.
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border bg-background/50 p-4">
              <Package className="h-6 w-6 text-teal-600 mb-2" />
              <h4 className="font-semibold text-sm">Asset Tracking</h4>
              <p className="text-xs text-muted-foreground mt-1">Maintain a complete register of all your fixed assets with detailed information.</p>
            </div>
            <div className="rounded-lg border bg-background/50 p-4">
              <Calculator className="h-6 w-6 text-orange-600 mb-2" />
              <h4 className="font-semibold text-sm">Depreciation</h4>
              <p className="text-xs text-muted-foreground mt-1">Calculate and post depreciation using multiple methods automatically.</p>
            </div>
            <div className="rounded-lg border bg-background/50 p-4">
              <ClipboardCheck className="h-6 w-6 text-green-600 mb-2" />
              <h4 className="font-semibold text-sm">Auditing</h4>
              <p className="text-xs text-muted-foreground mt-1">Conduct physical audits to verify asset existence and condition.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <StepByStepGuide steps={quickStartSteps} title="Quick Start Guide" />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Key Features Overview</CardTitle>
          <CardDescription>Explore the main capabilities of Access Assets</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <FeatureCard icon={Package} title="Asset Register" description="Comprehensive asset tracking with custom fields, photos, and documents." color="teal" />
            <FeatureCard icon={Receipt} title="Transaction Processing" description="Handle depreciation, additions, revaluations, and disposals." color="orange" />
            <FeatureCard icon={ClipboardCheck} title="Audit Management" description="Plan and execute physical asset audits with exception tracking." color="green" />
            <FeatureCard icon={LayoutDashboard} title="Dashboards & Reports" description="Real-time insights with customisable widgets and KPIs." color="purple" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function DashboardContent() {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-2">Dashboard</h2>
        <p className="text-muted-foreground">
          Your dashboard provides a real-time overview of your asset portfolio with customisable widgets and key performance indicators.
        </p>
      </div>

      <ScreenshotPlaceholder title="Dashboard Overview" aspectRatio="16/9" />

      <Card className="relative overflow-hidden border-2 border-purple-200 dark:border-purple-800">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-purple-500/5 opacity-40" />
        <CardHeader className="relative">
          <CardTitle className="flex items-center gap-2">
            <LayoutDashboard className="h-5 w-5 text-purple-600" />
            Dashboard Types
          </CardTitle>
          <CardDescription>Choose between different dashboard views based on your role</CardDescription>
        </CardHeader>
        <CardContent className="relative space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border bg-background/50 p-4">
              <h4 className="font-semibold">Asset Management Dashboard</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Focused on asset health, maintenance schedules, audit status, and lifecycle management. Ideal for asset managers and operations teams.
              </p>
            </div>
            <div className="rounded-lg border bg-background/50 p-4">
              <h4 className="font-semibold">Financial Dashboard</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Emphasises financial metrics including total asset value, depreciation trends, and cost analysis. Perfect for finance teams.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="kpis">
          <AccordionTrigger>Key Performance Indicators (KPIs)</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                KPIs provide at-a-glance metrics about your asset portfolio. Common KPIs include:
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  <span><strong>Total Asset Value:</strong> Sum of all asset net book values</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  <span><strong>Total Assets:</strong> Count of all assets in the register</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  <span><strong>Active Assets:</strong> Assets currently in use</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  <span><strong>Maintenance Due:</strong> Assets requiring maintenance</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  <span><strong>Audits Pending:</strong> Number of outstanding audits</span>
                </li>
              </ul>
              <ScreenshotPlaceholder title="KPI Widgets" aspectRatio="21/9" />
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="widgets">
          <AccordionTrigger>Available Widgets</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Customise your dashboard with various widgets to display the information most relevant to you:
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Assets by Category</h5>
                  <p className="text-xs text-muted-foreground">Pie chart showing asset distribution by category</p>
                </div>
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Assets by Location</h5>
                  <p className="text-xs text-muted-foreground">Bar chart displaying assets across locations</p>
                </div>
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Assets by Status</h5>
                  <p className="text-xs text-muted-foreground">Visual breakdown of asset statuses</p>
                </div>
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Net Book Value Trend</h5>
                  <p className="text-xs text-muted-foreground">Line chart showing NBV over time</p>
                </div>
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Depreciation Overview</h5>
                  <p className="text-xs text-muted-foreground">Year-to-date depreciation summary</p>
                </div>
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Insurance Renewals</h5>
                  <p className="text-xs text-muted-foreground">Upcoming insurance expiry dates</p>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="customisation">
          <AccordionTrigger>Customising Your Dashboard</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <StepByStepGuide
                title="How to Customise Widgets"
                steps={[
                  { step: 1, title: 'Open Customisation', description: "Click the 'Customise' button in the dashboard header.", hasScreenshot: true },
                  { step: 2, title: 'Select Widgets', description: 'Toggle widgets on or off based on your preferences.', hasScreenshot: true },
                  { step: 3, title: 'Save Changes', description: "Click 'Save' to apply your customised dashboard layout.", hasScreenshot: true },
                ]}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

function AssetsContent() {
  const createAssetSteps: StepGuide[] = [
    { step: 1, title: 'Navigate to Assets', description: "Click 'Assets' in the sidebar to open the asset register.", hasScreenshot: true },
    { step: 2, title: 'Click Add Asset', description: "Click the 'Add Asset' button in the top right corner.", hasScreenshot: true },
    { step: 3, title: 'Enter Basic Details', description: 'Fill in the Asset Code, Name, Description, Category, and Location fields.', hasScreenshot: true },
    { step: 4, title: 'Set Financial Values', description: 'Enter the Acquisition Cost, Acquisition Date, and configure depreciation settings.', hasScreenshot: true },
    { step: 5, title: 'Add Additional Information', description: 'Optionally add serial numbers, barcodes, supplier details, and attach documents or photos.', hasScreenshot: true },
    { step: 6, title: 'Save the Asset', description: "Click 'Save' to create the asset in your register.", hasScreenshot: true },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-2">Assets</h2>
        <p className="text-muted-foreground">
          The Assets module is the heart of Access Assets, where you manage your complete asset register.
        </p>
      </div>

      <ScreenshotPlaceholder title="Assets List View" aspectRatio="16/9" />

      <Card className="relative overflow-hidden border-2 border-teal-200 dark:border-teal-800">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-500/10 to-teal-500/5 opacity-40" />
        <CardHeader className="relative">
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-teal-600" />
            Asset Register Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="relative space-y-4">
          <p className="text-sm text-muted-foreground">
            The asset register displays all your fixed assets in either a gallery view (with images) or a table view (detailed list). You can filter, sort, and search to find specific assets quickly.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border bg-background/50 p-4">
              <h4 className="font-semibold text-sm">Gallery View</h4>
              <p className="text-xs text-muted-foreground mt-1">Visual card-based view showing asset images, ideal for quick identification.</p>
            </div>
            <div className="rounded-lg border bg-background/50 p-4">
              <h4 className="font-semibold text-sm">Table View</h4>
              <p className="text-xs text-muted-foreground mt-1">Detailed list view with customisable columns for comprehensive data analysis.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <StepByStepGuide steps={createAssetSteps} title="Creating a New Asset" />

      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="asset-details">
          <AccordionTrigger>Asset Detail Tabs</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Each asset has multiple tabs containing different types of information:
              </p>
              <div className="space-y-3">
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Details Tab</h5>
                  <p className="text-xs text-muted-foreground">Basic information including code, name, description, category, and location.</p>
                </div>
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Values Tab</h5>
                  <p className="text-xs text-muted-foreground">Financial information including acquisition cost, net book value, and depreciation settings.</p>
                </div>
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Settings Tab</h5>
                  <p className="text-xs text-muted-foreground">Depreciation method, rate, useful life, and nominal account mappings.</p>
                </div>
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Supplier Tab</h5>
                  <p className="text-xs text-muted-foreground">Supplier details, purchase order information, and warranty details.</p>
                </div>
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Documents & Photos Tab</h5>
                  <p className="text-xs text-muted-foreground">Attached files, images, and audit trail history.</p>
                </div>
              </div>
              <ScreenshotPlaceholder title="Asset Detail View" aspectRatio="16/9" />
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="filtering">
          <AccordionTrigger>Filtering and Searching</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Use the filter panel to narrow down your asset list:
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <Circle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Status Filter:</strong> Active, Pending, Inactive, Maintenance, Disposed</span>
                </li>
                <li className="flex items-start gap-2">
                  <Circle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Category Filter:</strong> Filter by asset category</span>
                </li>
                <li className="flex items-start gap-2">
                  <Circle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Location Filter:</strong> Filter by asset location</span>
                </li>
                <li className="flex items-start gap-2">
                  <Circle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span><strong>Search:</strong> Search by asset code, name, serial number, or description</span>
                </li>
              </ul>
              <ScreenshotPlaceholder title="Filter Panel" aspectRatio="4/3" />
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="bulk-actions">
          <AccordionTrigger>Bulk Actions</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Select multiple assets to perform bulk operations:
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  <span><strong>Export:</strong> Export selected assets to CSV or Excel</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  <span><strong>Print Labels:</strong> Generate barcode or QR code labels</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  <span><strong>Depreciation:</strong> Create a depreciation batch for selected assets</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  <span><strong>Disposal:</strong> Initiate disposal workflow for selected assets</span>
                </li>
              </ul>
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="import">
          <AccordionTrigger>Importing Assets</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Import assets in bulk using CSV files or sync from your accounting system:
              </p>
              <StepByStepGuide
                title="CSV Import Process"
                steps={[
                  { step: 1, title: 'Prepare Your CSV', description: 'Download the template and populate it with your asset data.', hasScreenshot: true },
                  { step: 2, title: 'Upload File', description: 'Navigate to Assets > Import and upload your CSV file.', hasScreenshot: true },
                  { step: 3, title: 'Map Columns', description: 'Match your CSV columns to the corresponding asset fields.', hasScreenshot: true },
                  { step: 4, title: 'Review & Import', description: 'Review the preview and click Import to create the assets.', hasScreenshot: true },
                ]}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

function TransactionsContent() {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-2">Transactions</h2>
        <p className="text-muted-foreground">
          Process financial transactions including depreciation, additions, revaluations, and disposals.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <FeatureCard icon={TrendingDown} title="Depreciation" description="Calculate and post periodic depreciation for your assets." color="orange" />
        <FeatureCard icon={Plus} title="Additions" description="Record new asset acquisitions and additions to existing assets." color="green" />
        <FeatureCard icon={RefreshCw} title="Revaluation" description="Adjust asset values to reflect current market conditions." color="blue" />
      </div>

      <Tabs defaultValue="depreciation" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="depreciation">Depreciation</TabsTrigger>
          <TabsTrigger value="additions">Additions</TabsTrigger>
          <TabsTrigger value="revaluation">Revaluation</TabsTrigger>
        </TabsList>

        <TabsContent value="depreciation" className="space-y-6 mt-6">
          <Card className="relative overflow-hidden border-2 border-orange-200 dark:border-orange-800">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-orange-500/5 opacity-40" />
            <CardHeader className="relative">
              <CardTitle className="flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-orange-600" />
                Depreciation Workflow
              </CardTitle>
              <CardDescription>Process depreciation in a controlled, auditable workflow</CardDescription>
            </CardHeader>
            <CardContent className="relative space-y-4">
              <p className="text-sm text-muted-foreground">
                The depreciation workflow ensures accurate calculations and proper approvals before posting to your financial system.
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">1. Prepare</Badge>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">2. Validate</Badge>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">3. Review</Badge>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">4. Approve</Badge>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">5. Post</Badge>
              </div>
            </CardContent>
          </Card>

          <ScreenshotPlaceholder title="Depreciation Batch Screen" aspectRatio="16/9" />

          <StepByStepGuide
            title="Running Depreciation"
            steps={[
              { step: 1, title: 'Create Batch', description: "Navigate to Transactions > Depreciation and click 'Create Batch'. Select the period and assets to include.", hasScreenshot: true },
              { step: 2, title: 'Validate Calculations', description: 'Review the calculated depreciation amounts. The system validates against your depreciation settings.', hasScreenshot: true },
              { step: 3, title: 'Review Journal Entries', description: 'Check the journal entries that will be posted to your financial system.', hasScreenshot: true },
              { step: 4, title: 'Obtain Approval', description: 'Submit for approval if required by your workflow settings.', hasScreenshot: true },
              { step: 5, title: 'Post to Ledger', description: "Click 'Post' to commit the depreciation entries to your financial system.", hasScreenshot: true },
            ]}
          />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Depreciation Methods</CardTitle>
              <CardDescription>Access Assets supports multiple depreciation calculation methods</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                See the <strong>Calculations</strong> section for detailed explanations and examples of each depreciation method.
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Straight-Line</h5>
                  <p className="text-xs text-muted-foreground">Equal depreciation each period</p>
                </div>
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Declining Balance</h5>
                  <p className="text-xs text-muted-foreground">Higher depreciation in early years</p>
                </div>
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Sum of Years Digits</h5>
                  <p className="text-xs text-muted-foreground">Accelerated based on remaining life</p>
                </div>
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Units of Production</h5>
                  <p className="text-xs text-muted-foreground">Based on actual usage</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="additions" className="space-y-6 mt-6">
          <Card className="relative overflow-hidden border-2 border-green-200 dark:border-green-800">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-green-500/5 opacity-40" />
            <CardHeader className="relative">
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-green-600" />
                Asset Additions
              </CardTitle>
              <CardDescription>Record new acquisitions and additions to existing assets</CardDescription>
            </CardHeader>
            <CardContent className="relative space-y-4">
              <p className="text-sm text-muted-foreground">
                Use the Additions module to record new asset purchases or add value to existing assets (e.g., upgrades, improvements).
              </p>
            </CardContent>
          </Card>

          <ScreenshotPlaceholder title="Additions Batch Screen" aspectRatio="16/9" />

          <StepByStepGuide
            title="Processing Additions"
            steps={[
              { step: 1, title: 'Create Addition Batch', description: "Navigate to Transactions > Additions and click 'Create Batch'.", hasScreenshot: true },
              { step: 2, title: 'Add Items', description: 'Add new assets or select existing assets to add value to.', hasScreenshot: true },
              { step: 3, title: 'Enter Values', description: 'Enter the acquisition cost and other financial details.', hasScreenshot: true },
              { step: 4, title: 'Review & Post', description: 'Review the entries and post to create the assets and journal entries.', hasScreenshot: true },
            ]}
          />
        </TabsContent>

        <TabsContent value="revaluation" className="space-y-6 mt-6">
          <Card className="relative overflow-hidden border-2 border-blue-200 dark:border-blue-800">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-500/5 opacity-40" />
            <CardHeader className="relative">
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-blue-600" />
                Asset Revaluation
              </CardTitle>
              <CardDescription>Adjust asset values to reflect current market conditions</CardDescription>
            </CardHeader>
            <CardContent className="relative space-y-4">
              <p className="text-sm text-muted-foreground">
                Revaluation allows you to adjust the book value of assets to reflect their current fair market value, either upward or downward.
              </p>
              <div className="rounded-lg border bg-amber-50 dark:bg-amber-950/20 p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <h5 className="font-medium text-sm text-amber-800 dark:text-amber-200">Important Note</h5>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                      Revaluations may have tax and accounting implications. Consult with your finance team before processing revaluations.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <ScreenshotPlaceholder title="Revaluation Batch Screen" aspectRatio="16/9" />

          <StepByStepGuide
            title="Processing Revaluations"
            steps={[
              { step: 1, title: 'Create Revaluation Batch', description: "Navigate to Transactions > Revaluation and click 'Create Batch'.", hasScreenshot: true },
              { step: 2, title: 'Select Assets', description: 'Choose the assets you want to revalue.', hasScreenshot: true },
              { step: 3, title: 'Enter New Values', description: 'Enter the new fair market value for each asset.', hasScreenshot: true },
              { step: 4, title: 'Review Changes', description: 'Review the revaluation amounts (increase or decrease).', hasScreenshot: true },
              { step: 5, title: 'Post Revaluation', description: 'Post to update asset values and create journal entries.', hasScreenshot: true },
            ]}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AuditsContent() {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-2">Audits</h2>
        <p className="text-muted-foreground">
          Plan and execute physical asset audits to verify existence, location, and condition of your assets.
        </p>
      </div>

      <ScreenshotPlaceholder title="Audit Dashboard" aspectRatio="16/9" />

      <Card className="relative overflow-hidden border-2 border-green-200 dark:border-green-800">
        <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-green-500/5 opacity-40" />
        <CardHeader className="relative">
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-green-600" />
            Audit Management Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="relative space-y-4">
          <p className="text-sm text-muted-foreground">
            The Audit module helps you maintain accurate asset records by conducting regular physical verifications. Track audit progress, record exceptions, and generate reports.
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border bg-background/50 p-4">
              <h4 className="font-semibold text-sm">Active Audits</h4>
              <p className="text-xs text-muted-foreground mt-1">View and manage audits currently in progress.</p>
            </div>
            <div className="rounded-lg border bg-background/50 p-4">
              <h4 className="font-semibold text-sm">Scheduled Audits</h4>
              <p className="text-xs text-muted-foreground mt-1">Plan future audits and set up recurring schedules.</p>
            </div>
            <div className="rounded-lg border bg-background/50 p-4">
              <h4 className="font-semibold text-sm">Audit History</h4>
              <p className="text-xs text-muted-foreground mt-1">Review completed audits and their findings.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <StepByStepGuide
        title="Creating and Running an Audit"
        steps={[
          { step: 1, title: 'Create New Audit', description: 'Navigate to Audits > Create and define the audit scope (all assets, by location, or by category).', hasScreenshot: true },
          { step: 2, title: 'Assign Auditors', description: 'Assign team members to conduct the physical verification.', hasScreenshot: true },
          { step: 3, title: 'Start the Audit', description: 'Begin the audit to enable asset verification.', hasScreenshot: true },
          { step: 4, title: 'Verify Assets', description: 'For each asset, confirm its existence, location, and condition. Use the mobile app for barcode scanning.', hasScreenshot: true },
          { step: 5, title: 'Record Exceptions', description: 'Flag any discrepancies such as missing assets, location mismatches, or damage.', hasScreenshot: true },
          { step: 6, title: 'Complete Audit', description: 'Review all findings and complete the audit to generate the final report.', hasScreenshot: true },
        ]}
      />

      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="audit-types">
          <AccordionTrigger>Audit Types</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <div className="grid gap-3">
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Full Audit</h5>
                  <p className="text-xs text-muted-foreground">Verify all assets in the register. Recommended annually.</p>
                </div>
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Location-Based Audit</h5>
                  <p className="text-xs text-muted-foreground">Audit assets at specific locations. Useful for site visits.</p>
                </div>
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Category-Based Audit</h5>
                  <p className="text-xs text-muted-foreground">Focus on specific asset categories (e.g., IT equipment).</p>
                </div>
                <div className="rounded-lg border p-3">
                  <h5 className="font-medium text-sm">Sample Audit</h5>
                  <p className="text-xs text-muted-foreground">Randomly selected assets for spot-checking.</p>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="exceptions">
          <AccordionTrigger>Managing Exceptions</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Exceptions are discrepancies found during an audit. Common exception types include:
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                  <span><strong>Missing Asset:</strong> Asset cannot be located</span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                  <span><strong>Location Mismatch:</strong> Asset found in different location than recorded</span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
                  <span><strong>Condition Issue:</strong> Asset damaged or in poor condition</span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                  <span><strong>Data Discrepancy:</strong> Asset details do not match records</span>
                </li>
              </ul>
              <ScreenshotPlaceholder title="Exception Management" aspectRatio="16/9" />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

function SettingsContent() {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-2">Settings & Administration</h2>
        <p className="text-muted-foreground">
          Configure system settings, manage categories, locations, and user permissions.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="relative overflow-hidden border-2 border-indigo-200 dark:border-indigo-800">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-indigo-500/5 opacity-40" />
          <CardHeader className="relative">
            <CardTitle className="text-base">Administration</CardTitle>
            <CardDescription>Manage core data and configurations</CardDescription>
          </CardHeader>
          <CardContent className="relative">
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                <span>Categories</span>
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                <span>Locations</span>
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                <span>Equipment Types</span>
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                <span>Disposal Reasons</span>
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                <span>Teams & Permissions</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-2 border-indigo-200 dark:border-indigo-800">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-indigo-500/5 opacity-40" />
          <CardHeader className="relative">
            <CardTitle className="text-base">System Settings</CardTitle>
            <CardDescription>Configure application behaviour</CardDescription>
          </CardHeader>
          <CardContent className="relative">
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                <span>Asset Options</span>
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                <span>Period Management</span>
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                <span>Integration Settings</span>
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                <span>Feature Flags</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>

      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="categories">
          <AccordionTrigger>Managing Categories</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Categories help organise your assets and can include default depreciation settings that are automatically applied to new assets.
              </p>
              <StepByStepGuide
                title="Creating a Category"
                steps={[
                  { step: 1, title: 'Navigate to Categories', description: 'Go to Administration > Categories.', hasScreenshot: true },
                  { step: 2, title: 'Click Add Category', description: "Click the 'Add Category' button.", hasScreenshot: true },
                  { step: 3, title: 'Enter Details', description: 'Enter the category name, description, and default depreciation settings.', hasScreenshot: true },
                  { step: 4, title: 'Save', description: 'Click Save to create the category.', hasScreenshot: true },
                ]}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="locations">
          <AccordionTrigger>Managing Locations</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Locations represent physical places where assets are stored or used. You can create a hierarchy of locations.
              </p>
              <StepByStepGuide
                title="Creating a Location"
                steps={[
                  { step: 1, title: 'Navigate to Locations', description: 'Go to Administration > Locations.', hasScreenshot: true },
                  { step: 2, title: 'Click Add Location', description: "Click the 'Add Location' button.", hasScreenshot: true },
                  { step: 3, title: 'Enter Details', description: 'Enter the location name, address, and optionally select a parent location.', hasScreenshot: true },
                  { step: 4, title: 'Save', description: 'Click Save to create the location.', hasScreenshot: true },
                ]}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="periods">
          <AccordionTrigger>Period Management</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Manage your financial periods for depreciation processing. Periods control when depreciation can be calculated and posted.
              </p>
              <div className="rounded-lg border bg-blue-50 dark:bg-blue-950/20 p-4">
                <div className="flex items-start gap-2">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                  <div>
                    <h5 className="font-medium text-sm text-blue-800 dark:text-blue-200">Period Status</h5>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                      Periods can be Open (accepting transactions), Closed (no new transactions), or Future (not yet active).
                    </p>
                  </div>
                </div>
              </div>
              <ScreenshotPlaceholder title="Period Management Screen" aspectRatio="16/9" />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

function GlossaryContent() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = Array.from(new Set(glossaryTerms.map(t => t.category)));

  const filteredTerms = glossaryTerms.filter(term => {
    const matchesSearch = term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
      term.definition.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || term.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-2">Glossary</h2>
        <p className="text-muted-foreground">
          Key terms and definitions used throughout Access Assets.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search terms..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={selectedCategory === null ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedCategory(null)}
          >
            All
          </Button>
          {categories.map(category => (
            <Button
              key={category}
              variant={selectedCategory === category ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid gap-4">
        {filteredTerms.map(term => (
          <Card key={term.term} className="relative overflow-hidden">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-base">{term.term}</CardTitle>
                <Badge variant="outline" className="shrink-0">{term.category}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{term.definition}</p>
            </CardContent>
          </Card>
        ))}
        {filteredTerms.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            No terms found matching your search.
          </div>
        )}
      </div>
    </div>
  );
}

function CalculationsContent() {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-2">Depreciation Calculations</h2>
        <p className="text-muted-foreground">
          Detailed explanations of depreciation methods with formulas and examples.
        </p>
      </div>

      <Card className="relative overflow-hidden border-2 border-red-200 dark:border-red-800">
        <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-red-500/5 opacity-40" />
        <CardHeader className="relative">
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-red-600" />
            Understanding Depreciation
          </CardTitle>
        </CardHeader>
        <CardContent className="relative space-y-4">
          <p className="text-sm text-muted-foreground">
            Depreciation is the systematic allocation of an asset's cost over its useful life. Different methods produce different expense patterns, which can affect financial statements and tax obligations.
          </p>
          <div className="rounded-lg border bg-background/50 p-4">
            <h4 className="font-semibold text-sm mb-2">Key Variables</h4>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li><strong>Cost:</strong> Original acquisition cost of the asset</li>
              <li><strong>Residual Value:</strong> Estimated value at end of useful life (also called salvage value)</li>
              <li><strong>Useful Life:</strong> Expected period the asset will be used</li>
              <li><strong>Depreciable Amount:</strong> Cost minus Residual Value</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-6">
        {calculationMethods.map(method => (
          <Card key={method.id} className="relative overflow-hidden">
            <CardHeader>
              <CardTitle className="text-lg">{method.name}</CardTitle>
              <CardDescription>{method.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg bg-muted/50 p-4">
                <h4 className="font-semibold text-sm mb-2">Formula</h4>
                <code className="text-sm bg-background px-2 py-1 rounded">{method.formula}</code>
              </div>

              <div className="rounded-lg border p-4">
                <h4 className="font-semibold text-sm mb-3">Example Calculation</h4>
                <div className="grid gap-2 md:grid-cols-3 mb-4">
                  <div className="text-sm">
                    <span className="text-muted-foreground">Asset Cost:</span>
                    <span className="ml-2 font-medium">£{method.example.assetCost.toLocaleString()}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-muted-foreground">Residual Value:</span>
                    <span className="ml-2 font-medium">£{method.example.salvageValue.toLocaleString()}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-muted-foreground">Useful Life:</span>
                    <span className="ml-2 font-medium">{method.example.usefulLife} years</span>
                  </div>
                </div>
                <Separator className="my-4" />
                <div className="space-y-1">
                  {method.example.calculations.map((calc, index) => (
                    <p key={index} className={cn('text-sm font-mono', calc === '' ? 'h-2' : '')}>
                      {calc}
                    </p>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Choosing the Right Method</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="rounded-lg border p-4">
              <h5 className="font-medium text-sm">Straight-Line</h5>
              <p className="text-xs text-muted-foreground mt-1">
                Best for: Assets that provide consistent value over time (buildings, furniture). Simple to calculate and understand.
              </p>
            </div>
            <div className="rounded-lg border p-4">
              <h5 className="font-medium text-sm">Declining Balance</h5>
              <p className="text-xs text-muted-foreground mt-1">
                Best for: Assets that lose value quickly in early years (vehicles, technology). Matches expense to benefit received.
              </p>
            </div>
            <div className="rounded-lg border p-4">
              <h5 className="font-medium text-sm">Sum of Years Digits</h5>
              <p className="text-xs text-muted-foreground mt-1">
                Best for: Similar to declining balance but with a more predictable pattern. Good for assets with higher early productivity.
              </p>
            </div>
            <div className="rounded-lg border p-4">
              <h5 className="font-medium text-sm">Units of Production</h5>
              <p className="text-xs text-muted-foreground mt-1">
                Best for: Manufacturing equipment, vehicles. Depreciation directly tied to actual usage rather than time.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function HelpPage(): React.ReactElement {
  const [activeSection, setActiveSection] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');

  const renderContent = () => {
    switch (activeSection) {
      case 'getting-started':
        return <GettingStartedContent />;
      case 'dashboard':
        return <DashboardContent />;
      case 'assets':
        return <AssetsContent />;
      case 'transactions':
        return <TransactionsContent />;
      case 'audits':
        return <AuditsContent />;
      case 'settings':
        return <SettingsContent />;
      case 'glossary':
        return <GlossaryContent />;
      case 'calculations':
        return <CalculationsContent />;
      default:
        return <GettingStartedContent />;
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="border-b bg-card p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <HelpCircle className="h-6 w-6 text-primary" />
              Help & Documentation
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Comprehensive guides, tutorials, and reference documentation for Access Assets
            </p>
          </div>
                    <div className="flex items-center gap-2">
                      <EvoFeatureFeedbackCollector feature="Help & Documentation" />
                      <Button variant="outline" size="sm">
                        <MessageCircle className="h-4 w-4 mr-2" />
                        Contact Support
                      </Button>
                      <Button variant="outline" size="sm">
                        <Mail className="h-4 w-4 mr-2" />
                        Email Us
                      </Button>
                    </div>
                  </div>
                </div>

            <div className="flex-1 overflow-hidden">
        <div className="flex h-full">
          <div className="w-72 border-r bg-muted/30 flex flex-col">
            <div className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search documentation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Separator />
            <ScrollArea className="flex-1 p-3">
              <div className="space-y-1">
                {docSections.map(section => (
                  <SectionNavItem
                    key={section.id}
                    section={section}
                    isActive={activeSection === section.id}
                    onClick={() => setActiveSection(section.id)}
                  />
                ))}
              </div>
            </ScrollArea>
            <Separator />
            <div className="p-4">
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-sm">Need more help?</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Contact our support team for personalised assistance.
                      </p>
                      <Button variant="link" size="sm" className="h-auto p-0 mt-2 text-xs">
                        Get in touch <ExternalLink className="h-3 w-3 ml-1" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="p-8 max-w-4xl">
                {renderContent()}
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>
    </div>
  );
}
